import React, { useState, useEffect } from 'react';
import './ProfilePage.css';

const ProfilePage = ({ userData, onLogout }) => {
  const [stats, setStats] = useState({
    activitiesCompleted: 0,
    totalMinutesSaved: 0,
    streak: 0,
  });

  const [settings, setSettings] = useState({
    notifications: true,
    dailyReminders: true,
    darkMode: false,
  });

  useEffect(() => {
    loadStats();
    loadSettings();
  }, []);

  const loadStats = () => {
    const data = localStorage.getItem('userStats');
    if (data) {
      setStats(JSON.parse(data));
    }
  };

  const loadSettings = () => {
    const data = localStorage.getItem('userSettings');
    if (data) {
      setSettings(JSON.parse(data));
    }
  };

  const handleSettingChange = (key) => {
    const newSettings = { ...settings, [key]: !settings[key] };
    setSettings(newSettings);
    localStorage.setItem('userSettings', JSON.stringify(newSettings));
  };

  const handleResetProgress = () => {
    if (
      window.confirm(
        'Are you sure you want to reset all your progress? This action cannot be undone.'
      )
    ) {
      localStorage.removeItem('userStats');
      localStorage.removeItem('completedActivities');
      setStats({
        activitiesCompleted: 0,
        totalMinutesSaved: 0,
        streak: 0,
      });
      alert('Your progress has been reset.');
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      onLogout();
    }
  };

  const achievements = [
    { id: 1, title: 'First Step', icon: '🚶', unlocked: stats.activitiesCompleted >= 1 },
    { id: 2, title: '10 Activities', icon: '🏆', unlocked: stats.activitiesCompleted >= 10 },
    { id: 3, title: '7 Day Streak', icon: '🔥', unlocked: stats.streak >= 7 },
    { id: 4, title: 'Time Master', icon: '⏰', unlocked: stats.totalMinutesSaved >= 100 },
    { id: 5, title: 'Dedicated', icon: '⭐', unlocked: stats.activitiesCompleted >= 50 },
    { id: 6, title: 'Legend', icon: '👑', unlocked: stats.streak >= 30 },
  ];

  return (
    <div className="profile-page">
      <div className="container">
        {/* Profile Header */}
        <div className="profile-header gradient-bg">
          <div className="profile-avatar">
            {userData?.name?.charAt(0).toUpperCase() || 'U'}
          </div>
          <h1>{userData?.name || 'User'}</h1>
          <p>{userData?.email || 'user@example.com'}</p>
        </div>

        {/* Stats Overview */}
        <div className="stats-overview">
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-value">{stats.activitiesCompleted}</div>
            <div className="stat-label">Activities Completed</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">🔥</div>
            <div className="stat-value">{stats.streak}</div>
            <div className="stat-label">Day Streak</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">⏰</div>
            <div className="stat-value">{stats.totalMinutesSaved}</div>
            <div className="stat-label">Minutes Reclaimed</div>
          </div>
        </div>

        {/* Achievements */}
        <section className="profile-section">
          <h2 className="section-title">Achievements</h2>
          <div className="achievements-grid">
            {achievements.map((achievement) => (
              <div
                key={achievement.id}
                className={`achievement-card ${achievement.unlocked ? 'unlocked' : 'locked'}`}
              >
                <div className="achievement-icon">{achievement.icon}</div>
                <div className="achievement-title">{achievement.title}</div>
                {achievement.unlocked && <div className="unlock-badge">Unlocked!</div>}
              </div>
            ))}
          </div>
        </section>

        {/* Settings */}
        <section className="profile-section">
          <h2 className="section-title">Settings</h2>

          <div className="settings-list">
            <div className="setting-item">
              <div className="setting-info">
                <div className="setting-icon">🔔</div>
                <div>
                  <div className="setting-title">Notifications</div>
                  <div className="setting-description">Receive push notifications</div>
                </div>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.notifications}
                  onChange={() => handleSettingChange('notifications')}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <div className="setting-icon">⏰</div>
                <div>
                  <div className="setting-title">Daily Reminders</div>
                  <div className="setting-description">Get reminded about brain rot</div>
                </div>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.dailyReminders}
                  onChange={() => handleSettingChange('dailyReminders')}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <div className="setting-icon">🌙</div>
                <div>
                  <div className="setting-title">Dark Mode</div>
                  <div className="setting-description">Enable dark theme</div>
                </div>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.darkMode}
                  onChange={() => handleSettingChange('darkMode')}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
        </section>

        {/* Resources */}
        <section className="profile-section">
          <h2 className="section-title">Resources</h2>

          <div className="menu-list">
            <button className="menu-item">
              <div className="menu-icon">ℹ️</div>
              <div className="menu-text">About Brain Rot</div>
              <div className="menu-arrow">›</div>
            </button>

            <button className="menu-item">
              <div className="menu-icon">📚</div>
              <div className="menu-text">Research & Studies</div>
              <div className="menu-arrow">›</div>
            </button>

            <button className="menu-item">
              <div className="menu-icon">❓</div>
              <div className="menu-text">Help & Support</div>
              <div className="menu-arrow">›</div>
            </button>

            <button className="menu-item">
              <div className="menu-icon">🔒</div>
              <div className="menu-text">Privacy Policy</div>
              <div className="menu-arrow">›</div>
            </button>
          </div>
        </section>

        {/* Danger Zone */}
        <section className="profile-section danger-zone">
          <h2 className="section-title danger-title">Danger Zone</h2>

          <button className="danger-btn" onClick={handleResetProgress}>
            <span className="danger-icon">🔄</span>
            <span>Reset Progress</span>
          </button>

          <button className="danger-btn" onClick={handleLogout}>
            <span className="danger-icon">🚪</span>
            <span>Logout</span>
          </button>
        </section>

        {/* App Info */}
        <div className="app-info">
          <p>Brain Rot v1.0.0</p>
          <p>Made with ❤️ for healthier digital habits</p>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;